import { type Dtos, isWipExtractableExtension } from "@inrev/common";
import { Option } from "@inrev/core";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { DateTime } from "luxon";
import type { FC } from "react";
import { formatEmpty, formatName } from "../../../utils";
import { Icon, Text } from "../../primitives";
import { CustomFileIcon } from "../../ui/CustomFileIcon";
import type { DynamicDataTableColumn } from "../../ui/DynamicDataTable/types";
import { ExtractionButton } from "../../uploaded-documents/ExtractionButton";
import type { UploadedDocumentRow } from "../../uploaded-documents/types";
import {
	UploadedDocumentRowActionsCell,
	type UploadedDocumentRowActionsConfig,
} from "../../uploaded-documents/UploadedDocumentRowActions";
import { WIP_REVIEW_TIMESTAMP_FORMAT, wipReviewDisplayLabel } from "./utils/wipReviewStatus";
import { WipReviewStatusIcon, type WipReviewStatusIconProps } from "./WipReviewStatusIcon";
import type { WipReviewQueueLastUpdated, WipReviewQueueRow } from "./wipReviewQueueRow";

const ACTION_BUTTON_WIDTH = 10;
const BASE_ACTION_COLUMN_SIZE = 80;
const EMPTY_ADMIN_USERS: Dtos.Admin.AdminUser.Get.Response[] = [];

const WipReviewQueueLastUpdatedCell: FC<{ lastUpdated?: WipReviewQueueLastUpdated }> = ({
	lastUpdated,
}) => {
	const { adminUsers = EMPTY_ADMIN_USERS } = useAdminFetchAdminUsers();

	if (!lastUpdated) {
		return (
			<Text type="body" color="secondary" data-testid="wip-review-queue-last-updated-cell">
				{formatEmpty(undefined)}
			</Text>
		);
	}

	const dateTime = DateTime.fromISO(lastUpdated.modifiedAt);
	const timestamp = dateTime.isValid
		? dateTime.toFormat(WIP_REVIEW_TIMESTAMP_FORMAT)
		: formatEmpty(undefined);
	const editorName = lastUpdated.modifiedByUserId
		? formatName(adminUsers.find((user) => user.id === lastUpdated.modifiedByUserId))
		: undefined;

	return (
		<div className="flex flex-col gap-2.5" data-testid="wip-review-queue-last-updated-cell">
			<Text type="body" color="secondary">
				{timestamp}
			</Text>
			<Text type="body" color="secondary">
				{formatEmpty(editorName)}
			</Text>
		</div>
	);
};

export type WipReviewQueueColumnsRetryConfig = {
	onTriggerExtraction: (row: UploadedDocumentRow) => void;
	extractionPending: boolean;
};

export type WipReviewQueueColumnsConfig = {
	actions: UploadedDocumentRowActionsConfig<UploadedDocumentRow>;
	retryConfig: WipReviewQueueColumnsRetryConfig;
};

function toUploadedDocumentRow(row: WipReviewQueueRow): UploadedDocumentRow {
	const { attachment, ...wipExtraction } = row;
	return {
		...attachment,
		wipExtraction,
	};
}

export function wipReviewStatusIconProps(row: WipReviewQueueRow): WipReviewStatusIconProps {
	return {
		reviewStatus: row.reviewStatus,
		hasCorrections: row.hasCorrections,
		extractionStatus: row.status,
		errorType: row.errorType,
		errorMessage: row.errorMessage,
		extractionTriggeredAt: Option.mapOrElse(
			row.extractionTriggeredAt,
			(triggeredAt) => DateTime.fromISO(triggeredAt),
			undefined,
		),
		extractionCompletedAt: Option.mapOrElse(
			row.extractionCompletedAt,
			(completedAt) => DateTime.fromISO(completedAt),
			undefined,
		),
		underwriterReviewedAt: Option.mapOrElse(
			row.underwriterReviewedAt,
			(reviewedAt) => DateTime.fromISO(reviewedAt),
			undefined,
		),
		underwriterReviewedByUserId: row.underwriterReviewedByUserId,
		extractionConfidence: row.extractionConfidence,
		signals: row.signals,
	};
}

export function createWipReviewQueueColumns({
	actions,
	retryConfig,
}: WipReviewQueueColumnsConfig): DynamicDataTableColumn<WipReviewQueueRow>[] {
	const actionColumnSize = BASE_ACTION_COLUMN_SIZE + ACTION_BUTTON_WIDTH;

	return [
		{
			id: "action_menu",
			header: "Actions",
			size: actionColumnSize,
			enableSorting: false,
			cell: ({ row }) => {
				const uploadedDocumentRow = toUploadedDocumentRow(row.original);
				return (
					<div className="flex justify-center" onClick={(event) => event.stopPropagation()}>
						<UploadedDocumentRowActionsCell
							row={uploadedDocumentRow}
							{...actions}
							leadingSlot={
								<ExtractionButton
									row={uploadedDocumentRow}
									onTrigger={retryConfig.onTriggerExtraction}
									extractionStatus={row.original.status}
									triggerPending={retryConfig.extractionPending}
									hasExistingExtraction
									isExtensionSupported={isWipExtractableExtension}
								/>
							}
						/>
					</div>
				);
			},
		},
		{
			id: "status",
			accessorFn: (row) => wipReviewDisplayLabel(row.reviewStatus, row.hasCorrections),
			header: "Status",
			size: 180,
			enableSorting: true,
			cell: ({ row }) => <WipReviewStatusIcon {...wipReviewStatusIconProps(row.original)} />,
		},
		{
			id: "file",
			accessorFn: (row) => row.attachment.file.name,
			header: "File",
			size: 220,
			minSize: 200,
			enableSorting: true,
			enableResizing: true,
			cell: ({ row }) => (
				<span className="inline-flex min-w-0 items-center gap-2">
					<CustomFileIcon extension={row.original.attachment.file.extension} />
					<Text type="body" color="secondary" truncate>
						{row.original.attachment.file.name}
					</Text>
				</span>
			),
		},
		{
			id: "statementDate",
			accessorFn: (row) =>
				row.statementDate ? DateTime.fromISO(row.statementDate).toMillis() : undefined,
			header: "Statement Date",
			size: 140,
			sortingFn: "datetime",
			enableSorting: true,
			cell: ({ row }) => {
				const statementDate = row.original.statementDate;
				if (!statementDate) {
					return (
						<span className="inline-flex items-center gap-1.5">
							<Icon type="exclamation-triangle" size="mini" color="warning" />
							<Text type="body" color="secondary">
								Undated
							</Text>
						</span>
					);
				}
				const dateTime = DateTime.fromISO(statementDate);
				return (
					<Text type="body" color="secondary">
						{dateTime.isValid ? dateTime.toFormat("MM/dd/yyyy") : formatEmpty(undefined)}
					</Text>
				);
			},
		},
		{
			id: "uploadedAt",
			accessorFn: (row) => row.attachment.createdAtMs,
			header: "Uploaded At",
			size: 110,
			sortDescFirst: true,
			enableSorting: true,
			cell: ({ row }) => (
				<Text type="body" color="secondary">
					{DateTime.fromMillis(row.original.attachment.createdAtMs).toFormat("MM/dd/yyyy")}
				</Text>
			),
		},
		{
			id: "assignedProjects",
			accessorFn: (row) => (row.projectCount === 0 ? 0 : row.assignedCount / row.projectCount),
			header: "Assigned Projects",
			size: 160,
			enableSorting: true,
			cellStyle: { centerItems: true },
			cell: ({ row }) => (
				<Text type="body" color="secondary" data-testid="wip-review-queue-assigned-projects-cell">
					{`${row.original.assignedCount}/${row.original.projectCount}`}
				</Text>
			),
		},
		{
			id: "lastUpdated",
			accessorFn: (row) =>
				row.lastUpdated ? DateTime.fromISO(row.lastUpdated.modifiedAt).toMillis() : undefined,
			header: "Last updated",
			size: 160,
			sortingFn: "datetime",
			enableSorting: true,
			cell: ({ row }) => <WipReviewQueueLastUpdatedCell lastUpdated={row.original.lastUpdated} />,
		},
	];
}
